import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { endorsementsApi } from '../services/endorsements';
import { getApiErrorMessage } from '../utils/errors';

interface EndorseModalProps {
  isOpen: boolean;
  onClose: () => void;
  providerName: string;
  providerId?: number | null;
  // Called after an endorsement is saved, e.g. so the caller can refresh its lists
  onEndorsed?: () => void;
}

const skills = [
  'Facial Anatomy',
  'Injection Technique',
  'Laser & Devices',
  'Skin Care',
  'Complication Management',
  'Cosmetic Surgery',
  'Patient Management',
  'Social Media',
  'Practice Management',
  'Education',
  'Industry Trainer',
  'Industry Speaker',
];

const EndorseModal: React.FC<EndorseModalProps> = ({
  isOpen,
  onClose,
  providerName,
  providerId,
  onEndorsed,
}) => {
  const [selectedSkills, setSelectedSkills] = useState<string[]>([]);
  const [showSuccess, setShowSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const toggleSkill = (skill: string) => {
    setSelectedSkills((prev) =>
      prev.includes(skill) ? prev.filter((s) => s !== skill) : [...prev, skill]
    );
  };

  const handleSubmit = async () => {
    if (!providerId) {
      setError('Unable to endorse this provider right now. Please try again later.');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      await endorsementsApi.create({
        endorsed_id: providerId,
        skills_endorsed: selectedSkills.join(', '),
      });
      setShowSuccess(true);
      onEndorsed?.();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to submit endorsement'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setShowSuccess(false);
    setSelectedSkills([]);
    setError(null);
    onClose();
  };

  const handleEndorseOthers = () => {
    handleClose();
  };

  const handleBackToHomepage = () => {
    handleClose();
    navigate('/dashboard');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg max-w-md w-full mx-4 relative">
        <div className="px-8 py-10">
          {!showSuccess ? (
            <>
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-['Ovo'] text-xl text-[#1f1f1f] uppercase">
                  ENDORSE {providerName}
                </h2>
                <button
                  onClick={handleClose}
                  className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M18 6L6 18"
                      stroke="#1f1f1f"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M6 6L18 18"
                      stroke="#1f1f1f"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>

              {/* Question */}
              <div className="mb-4">
                <p className="font-['Open_Sans'] font-semibold text-base text-[#1f1f1f]">
                  What skills are you endorsing {providerName} for?
                </p>
              </div>

              {/* Skills Grid */}
              <div className="flex flex-wrap gap-3 mb-6">
                {skills.map((skill) => (
                  <button
                    key={skill}
                    onClick={() => toggleSkill(skill)}
                    className={`px-3 py-2 font-['Open_Sans'] text-sm transition-all ${
                      selectedSkills.includes(skill)
                        ? 'bg-[#c6a664] text-white'
                        : 'bg-white text-[#1f1f1f] border border-[#c6a664]'
                    }`}
                  >
                    {skill}
                  </button>
                ))}
              </div>

              {error && (
                <p className="font-['Open_Sans'] text-sm text-red-600 mb-4" role="alert">
                  {error}
                </p>
              )}

              {/* Submit Button */}
              <button
                onClick={handleSubmit}
                disabled={selectedSkills.length === 0 || submitting}
                className="w-full px-6 py-3 bg-white border border-[#1f1f1f] text-[#1f1f1f] font-['Ovo'] text-xl uppercase hover:bg-[#1f1f1f] hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M20 6L9 17L4 12"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                {submitting ? 'SUBMITTING...' : 'SUBMIT ENDORSEMENT'}
              </button>
            </>
          ) : (
            <>
              {/* Success View */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-['Ovo'] text-xl text-[#1f1f1f] uppercase text-center w-full">
                  SUCCESSFUL ENDORSEMENT!
                </h2>
                <button
                  onClick={handleClose}
                  className="p-1 hover:bg-gray-100 rounded-full transition-colors absolute right-0 top-0"
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M18 6L6 18"
                      stroke="#1f1f1f"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M6 6L18 18"
                      stroke="#1f1f1f"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>

              {/* Success Message */}
              <div className="text-center mb-6">
                <p className="font-['Open_Sans'] text-base text-[#1f1f1f] leading-relaxed">
                  Thanks for endorsing {providerName}!<br />
                  Your endorsement will help them build CLOUT!
                </p>
              </div>

              {/* Celebration Image */}
              <div className="flex justify-center mb-8">
                <div className="w-48 h-64 relative">
                  {/* Simplified celebration graphic */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <div className="w-32 h-32 bg-[#f6e5df] rounded-full flex items-center justify-center mb-4">
                        <svg
                          width="60"
                          height="60"
                          viewBox="0 0 60 60"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M30 5L36.18 18.82L51 21.04L40.5 31.26L43.02 46L30 39.32L16.98 46L19.5 31.26L9 21.04L23.82 18.82L30 5Z"
                            fill="#c6a664"
                            stroke="#c6a664"
                            strokeWidth="2"
                          />
                        </svg>
                      </div>
                      <div className="text-6xl">🎉</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-4">
                <button
                  onClick={handleEndorseOthers}
                  className="w-full px-6 py-3 bg-[#1f1f1f] text-white font-['Ovo'] text-xl uppercase hover:bg-gray-800 transition-colors"
                >
                  ENDORSE OTHERS
                </button>
                <button
                  onClick={handleBackToHomepage}
                  className="w-full px-6 py-3 bg-white border border-[#1f1f1f] text-[#1f1f1f] font-['Ovo'] text-xl uppercase hover:bg-[#1f1f1f] hover:text-white transition-colors"
                >
                  BACK TO HOMEPAGE
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default EndorseModal;
